import { defineStore } from 'pinia';
import { ref, shallowRef } from 'vue';

export interface ConfirmRequest {
  title: string;
  /** Plain sentence describing what will happen. */
  body: string;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Styles the confirm button as destructive. */
  danger?: boolean;
  /**
   * Require the player to type this exact word first. Reserve it for things
   * that cannot be undone, like losing a village.
   */
  typeToConfirm?: string;
}

/**
 * Promise-based confirmation.
 *
 * `const ok = await confirm.ask({...})` reads like a normal branch at the call
 * site, so guarding an action never restructures the code around it.
 */
export const useConfirmStore = defineStore('confirm', () => {
  const request = shallowRef<ConfirmRequest | null>(null);
  const typed = ref('');
  let resolver: ((ok: boolean) => void) | null = null;

  function ask(next: ConfirmRequest): Promise<boolean> {
    // A second prompt while one is open cancels the first rather than
    // stranding its promise forever.
    resolver?.(false);
    typed.value = '';
    request.value = next;
    return new Promise<boolean>((resolve) => {
      resolver = resolve;
    });
  }

  function settle(ok: boolean): void {
    request.value = null;
    typed.value = '';
    const resolve = resolver;
    resolver = null;
    resolve?.(ok);
  }

  return {
    request,
    typed,
    ask,
    confirm: () => settle(true),
    cancel: () => settle(false),
  };
});
